import { useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { LockKey, CheckCircle } from "@phosphor-icons/react";

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const forced = !!user?.force_password_change;

  const submit = async (e) => {
    e?.preventDefault();
    if (form.next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (form.next !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await API.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password updated");
      if (checkAuth) await checkAuth();
      // Route operatives to their portal, others home
      navigate(["in_house", "vendor_operative"].includes(user?.role) ? "/my-work" : "/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-6">
      <form onSubmit={submit} className="max-w-md w-full bg-white dark:bg-slate-900 rounded-lg shadow p-8 space-y-5"
            data-testid="change-password-form">
        <div className="text-center">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/40">
            <LockKey className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="mt-4 text-xl font-bold text-slate-900 dark:text-slate-100">
            {forced ? "Set your password" : "Change password"}
          </h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {forced
              ? "Welcome! Please choose a new password to finish setting up your account."
              : "Update the password for your account."}
          </p>
        </div>

        <div>
          <Label>Current password</Label>
          <Input type="password" value={form.current}
                 onChange={(e) => setForm({ ...form, current: e.target.value })}
                 placeholder={forced ? "The temporary password from your welcome email" : ""}
                 autoFocus required data-testid="change-password-current" />
        </div>
        <div>
          <Label>New password</Label>
          <Input type="password" value={form.next}
                 onChange={(e) => setForm({ ...form, next: e.target.value })}
                 minLength={8} required data-testid="change-password-new" />
          <p className="text-xs text-slate-500 mt-1">At least 8 characters.</p>
        </div>
        <div>
          <Label>Confirm new password</Label>
          <Input type="password" value={form.confirm}
                 onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                 required data-testid="change-password-confirm" />
        </div>

        <Button type="submit" disabled={saving} className="w-full" data-testid="change-password-submit">
          {saving ? "Saving…" : (<><CheckCircle className="h-4 w-4 mr-2" /> Save new password</>)}
        </Button>
      </form>
    </div>
  );
}
